/**
 * useInstitutionFlags Hook
 *
 * Sprint C14 Lane A (WP A-1) -- tenant-scoped product-surface flags.
 * Consolidated with useFeatureFlags (Sprint C14 Lane A WP dedup) to serve
 * as the single hook for all feature flag lookups in admin-portal.
 *
 * Two flag categories are served:
 *
 * 1. Tenant product-surface flags (InstitutionFlags):
 *    Read from `GET /api/admin/tenants/me/feature-flags`. These are Boolean
 *    columns on `Institution` that gate whole nav groups and API route groups:
 *      - enableLosPreview         -- Lending (Preview) nav group + /api/admin/loans/*
 *      - enablePeValuation        -- PE Valuation nav group + /api/admin/pe/*
 *      - enableLoanParticipations   -- Loan Participations nav group + /api/v1/syndication/*
 *      - palEnabled               -- PAL entitlement flag
 *    Architectural decision (Sprint C14 README #2): these are named booleans,
 *    not a generic key/value store. There are exactly four surface flags and
 *    they map 1:1 to product surfaces.
 *
 * 2. Platform feature flags (FeatureFlag[]):
 *    Read from `GET /settings/feature-flags[/{tenantId}]`. These are
 *    key-value toggles for fine-grained asset-class and system-level gating
 *    (e.g. `collateral.asset_class.{taxonomyNodeId}`). The `isEnabled(key)`
 *    and `getFlagsByCategory(category)` helpers operate on this list.
 *
 * The endpoint for (1) does NOT degrade silently. A failed read surfaces as
 * `flagsUnreadable` / `flagsError`, distinct from a successful read of
 * all-false; `flags` still exposes the all-OFF placeholder so nav rendering
 * never crashes, but a consumer that treats a `false` as an entitlement
 * DECISION must check `flagsUnreadable` first (GH #2742 class).
 *
 * IMPORTANT -- impersonation coupling:
 *
 * The React Query cache key for category (1) is
 * `['institution-flags', tenantId || 'apex']`. When a platform admin switches
 * impersonation contexts, the key changes:
 *
 *   - Impersonating FI tenant T   -> key = ['institution-flags', T.id]
 *                                  -> flags are FI T's Institution row
 *   - Switch impersonation OFF    -> key = ['institution-flags', 'apex']
 *                                  -> flags are the APEX Institution row
 *
 * Nav-group visibility (Lending Preview, PE Valuation, Loan Participations)
 * is therefore controlled by whichever Institution row matches the active
 * cache key. The APEX Institution row's flag values directly control which
 * nav groups appear when no impersonation is active.
 *
 * Consequence: if the apex row's flags diverge from operator expectations
 * (e.g. apex has `enableLosPreview = false` but the operator expects to
 * see the Lending nav group at all times), switching impersonation OFF
 * will hide the nav group. The fix is to update the apex row's flag,
 * not to special-case the hook -- the hook honors whatever the active
 * tenant's Institution row says.
 *
 * Seeding discipline: keep the apex Institution row's product-surface
 * flags aligned with the union of features the platform admin needs to
 * operate. The apex row is the operator's "home" tenant when not
 * impersonating; it should not be treated as a placeholder.
 */

import { useQuery } from '@tanstack/react-query';
import type { components } from '@aaimplatform/institutional-types/generated/platform-admin';
import { useAuthStore } from '../stores/auth';
import { platformAdminClient } from '../api/client';

/** Platform-level feature flag -- key/value toggle from /settings/feature-flags. */
export type FeatureFlag = components['schemas']['FeatureFlag'];

/**
 * Shape of the response from `GET /api/admin/tenants/me/feature-flags`.
 *
 * Sprint C15-C WP D-2 (2026-05-02): extended with `palEnabled` so the four
 * tenant-scoped entitlements (PAL, LOS Preview, PE Valuation, Liquidity
 * Network) are addressable from a single hook. Mirrors the canonical
 * `InstitutionFeatureFlags` schema in
 * `@aaimplatform/institutional-types/platform-admin`. Local definition
 * preserved (rather than aliased to `components['schemas']['InstitutionFeatureFlags']`)
 * so the graceful-degrade default remains a stable shape independent of
 * the generated types' optional-vs-required nuances.
 */
export interface InstitutionFlags {
  palEnabled: boolean;
  enableLosPreview: boolean;
  enablePeValuation: boolean;
  enableLoanParticipations: boolean;
  enableOverdraftPreview: boolean;
}

/**
 * All-OFF placeholder for the LOADING window only.
 *
 * GH #2742 class. This used to be returned on every failure path as well, so a
 * 500, a network drop, or an unparseable body produced four confident `false`
 * entitlement facts that four consumers rendered as measured: the dashboard hid
 * the participations tile, the entitlements card drew neutral badges plus a
 * "Request activation" CTA for services the FI may already hold, and the PE
 * route gate redirected an entitled operator away from /pe/*. An unreadable
 * entitlement is NOT a withheld entitlement. `fetchInstitutionFlags` now throws
 * on every failure path, so `isError` is the distinct third state, and any
 * consumer treating a `false` as a decision must check it first.
 */
export const DEFAULT_INSTITUTION_FLAGS: InstitutionFlags = {
  palEnabled: false,
  enableLosPreview: false,
  enablePeValuation: false,
  enableLoanParticipations: false,
  enableOverdraftPreview: false,
};

const FLAGS_ENDPOINT = '/api/admin/tenants/me/feature-flags';

/**
 * Error thrown when the entitlement read did not produce a body. `status` rides
 * along so a consumer can say WHICH failure it hit without re-parsing anything.
 */
export class InstitutionFlagsReadError extends Error {
  readonly status: number | null;
  constructor(message: string, status: number | null) {
    super(message);
    this.name = 'InstitutionFlagsReadError';
    this.status = status;
  }
}

/**
 * Fetch tenant feature flags. THROWS on every failure path.
 *
 * The previous contract returned DEFAULT_INSTITUTION_FLAGS on a network error,
 * on any non-200, and on an unparseable body -- which made "the read failed"
 * and "this FI holds no entitlements" the same value. Those are different
 * facts, an FI operator acts differently on each, and only the caller can
 * decide how to degrade. So the failure is propagated and React Query surfaces
 * it as `isError`; consumers branch on it explicitly.
 */
async function fetchInstitutionFlags(): Promise<InstitutionFlags> {
  // N6 cookie cutover (SEC-V04): authenticated by the HttpOnly session cookie
  // (credentials: 'include'); the SPA attaches no bearer.
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  let response: Response;
  try {
    response = await fetch(FLAGS_ENDPOINT, { method: 'GET', headers, credentials: 'include' });
  } catch (cause) {
    throw new InstitutionFlagsReadError(
      cause instanceof Error
        ? `Entitlements could not be read: ${cause.message}`
        : 'Entitlements could not be read: the request did not complete.',
      null,
    );
  }
  if (!response.ok) {
    // A 404 here is NOT "no entitlements" -- it is an unreachable endpoint, and
    // saying so is the whole point of this branch.
    throw new InstitutionFlagsReadError(
      `Entitlements could not be read (HTTP ${response.status}).`,
      response.status,
    );
  }
  let body: Partial<InstitutionFlags>;
  try {
    body = (await response.json()) as Partial<InstitutionFlags>;
  } catch {
    throw new InstitutionFlagsReadError(
      `Entitlements could not be read: the response body was not valid JSON (HTTP ${response.status}).`,
      response.status,
    );
  }
  return {
    palEnabled: !!body.palEnabled,
    enableLosPreview: !!body.enableLosPreview,
    enablePeValuation: !!body.enablePeValuation,
    enableLoanParticipations: !!body.enableLoanParticipations,
    enableOverdraftPreview: !!body.enableOverdraftPreview,
  };
}

/**
 * React Query hook returning the three tenant-scoped feature flags.
 *
 * Always returns a stable `InstitutionFlags` shape -- never undefined --
 * so consumers can rely on the booleans during the loading window. The
 * `isLoading` flag is exposed for callers that want to defer rendering
 * until the first response, but the default behavior is to render with
 * `DEFAULT_INSTITUTION_FLAGS` immediately and refresh when the query
 * resolves.
 */
export function useInstitutionFlags() {
  const user = useAuthStore((s) => s.user);
  // Platform-admin tokens carry no tenantId claim (they resolve to APEX
  // server-side via resolveEffectiveTenantId). Enable for any authenticated
  // user so the apex tenant's flags are fetched and the conditional nav
  // groups render correctly for platform-admin sessions.
  const enabled = !!user;
  const effectiveTenantId = user?.tenantId ?? '';

  // Query 1: Tenant product-surface flags (4 named booleans).
  //
  // The 'apex' literal below is a CLIENT-SIDE React Query cache-key
  // namespace label -- it is NOT a tenantId value sent to the backend.
  // The fetchInstitutionFlags() call above issues
  // GET /api/admin/tenants/me/feature-flags which is resolved server-side
  // from the JWT via resolveEffectiveTenantId. 'apex' here only
  // distinguishes the impersonation-OFF cache slot from per-FI-tenant
  // cache slots in React Query's internal cache. See the
  // impersonation-coupling section of the file-level JSDoc above for the
  // full cache-key contract.
  const institutionQuery = useQuery<InstitutionFlags>({
    queryKey: ['institution-flags', effectiveTenantId || 'apex'],
    queryFn: fetchInstitutionFlags,
    enabled,
    // Conservative: 1 minute stale time. Flags rarely change at runtime;
    // they are flipped via tenant configuration and an explicit reload is
    // expected to surface the new state.
    staleTime: 60 * 1000,
    // No retry storm, and no silent default either: fetchInstitutionFlags
    // throws, so one failed attempt lands in `isError` and the consumers
    // branch on it.
    retry: false,
  });

  // Query 2: Platform feature flags (key/value list for asset-class and
  // system-level gating). Endpoint varies by whether a tenantId is present:
  // with tenantId -> merged platform+tenant flags; without -> platform only.
  //
  // BOTH variants go through `platformAdminClient`, whose baseUrl is
  // `/api/admin` -- which is what the backend mounts
  // (`app.use('/api/admin/settings/feature-flags', ...)`, platform-admin-api
  // server.ts; the tenant leaf is `GET /:tenantId` in
  // routes/feature-flag.routes.ts).
  //
  // The tenant-scoped variant used to build the raw path
  // `/settings/feature-flags/{tenantId}` and hand it to a bare `fetch`. That
  // string is missing the `/api/admin` prefix the typed client supplies, so on
  // prod it matched no LB rule and no nginx location and was served the SPA's
  // own index.html: HTTP 200, content-type text/html, `response.json()` throws,
  // the catch returned `[]`, and every `isEnabled()` read false with nothing
  // logged. The path has been in the canonical spec
  // (`schema-registry/platform-admin.openapi.yaml` -> `getTenantFeatureFlags`)
  // and in the generated `paths` map the whole time; only this call site was
  // bypassing it.
  const platformFlagsQuery = useQuery<FeatureFlag[]>({
    queryKey: ['feature-flags', effectiveTenantId],
    queryFn: async (): Promise<FeatureFlag[]> => {
      if (effectiveTenantId) {
        const { data, error } = await platformAdminClient.GET(
          '/settings/feature-flags/{tenantId}',
          { params: { path: { tenantId: effectiveTenantId } } },
        );
        if (error) return [];
        return (data as FeatureFlag[]) ?? [];
      }
      const { data, error } = await platformAdminClient.GET('/settings/feature-flags');
      if (error) return [];
      return (data as FeatureFlag[]) ?? [];
    },
    enabled,
    retry: 1,
  });

  const platformFlags = platformFlagsQuery.data ?? [];

  const isEnabled = (key: string): boolean => {
    const flag = platformFlags.find((f) => f.key === key);
    return flag?.enabled ?? false;
  };

  const getFlagsByCategory = (category: string): FeatureFlag[] => {
    return platformFlags.filter((f) => f.category === category);
  };

  return {
    /**
     * The read result, or the all-OFF placeholder while loading / after a
     * failure. SAFE to render as chrome; NOT safe to read as an entitlement
     * decision without checking `flagsUnreadable`.
     */
    flags: institutionQuery.data ?? DEFAULT_INSTITUTION_FLAGS,
    /** The read result, or null when it has not been read. No placeholder. */
    flagsRead: institutionQuery.data ?? null,
    /**
     * The tenant entitlement read specifically -- deliberately NOT the combined
     * `isError` below, which also fires for the unrelated platform key/value
     * flag query. An entitlement surface must not claim its entitlements are
     * unreadable because a different endpoint failed.
     */
    flagsUnreadable: institutionQuery.isError,
    flagsLoading: institutionQuery.isLoading,
    flagsError: institutionQuery.error ?? null,
    isLoading: institutionQuery.isLoading || platformFlagsQuery.isLoading,
    isError: institutionQuery.isError || platformFlagsQuery.isError,
    refetch: institutionQuery.refetch,
    isEnabled,
    getFlagsByCategory,
  };
}
